import '@rxap/rxjs';
import { Inject, Injectable, InjectionToken, isDevMode, Optional } from '@angular/core';
import { TableEvent } from 'eurogard-data-source/table';
import { Method, MethodWithParameters } from 'eurogard-pattern';

export interface GetByFilterMethodParameters {
  page: number;
  size: number;
  sort: string;
  order: string;
  filter: string;
  company?: string;
}

export interface GetByFilterMethodParameter<P = GetByFilterMethodParameters> extends Record<string, unknown> {
  parameters: P;
}

export interface GetByFilterMethodResponse<T = unknown> {
  maxCount?: number;
  size?: number;
  page?: number;
  entities: Array<T>;
  additionalInformation?: string;
}

export const GET_BY_FILTER_TO_TABLE_METHOD_INJECTION_TOKEN = new InjectionToken(
  'GET_BY_FILTER_TO_TABLE_METHOD_INJECTION_TOKEN');

@Injectable()
export class GetByFilterToTableMethod<T = unknown, P = GetByFilterMethodParameters> implements Method<T[], TableEvent> {

  private fixedFilter: Record<string, string> = {};

  constructor(
    @Inject(GET_BY_FILTER_TO_TABLE_METHOD_INJECTION_TOKEN)
    protected getByFilter: MethodWithParameters<GetByFilterMethodResponse<T>, GetByFilterMethodParameter<P>>,
  ) {
  }

  setFixedFilter(filter: Record<string, string>) {
    this.fixedFilter = filter;
  }

  public async call(event: TableEvent): Promise<T[]> {
    if (isDevMode()) {
      console.log('event', event);
    }
    const response = await this.getByFilter.call({
      parameters: await this.eventToParameters(event),
    });
    event?.setTotalLength!(response.maxCount ?? 0);
    return response.entities ?? [];
  }

  // eslint-disable-next-line complexity
  public async eventToParameters(event: TableEvent): Promise<P> {
    if (!event.filter || typeof event.filter === 'object') {
      event.filter ??= {} as Record<string, any>;
      (event.filter as any)['__archived'] ??= false;
    }
    const mergedFilter = {
      ...((typeof event.filter === 'object' && event.filter !== null) ? event.filter : {}),
      ...this.fixedFilter
    };
    return {
      ...event.parameters ?? {},
      page: event.page?.pageIndex ?? 0,
      size: event.page?.pageSize ?? 10,
      sort: event.sort?.active ?? '__updatedAt',
      order: event.sort?.direction || 'desc', // use || instead of ?? because direction can be ' ' (empty string)
      filter: Object.entries(mergedFilter)
                    .filter(([ _, value ]) => value !== null && value !== undefined && `${ value }` !== '')
                    .map(([ key, value ]) => [ key, value ].join('%3A'))
                    .join('%3B')
    } as any; // TODO : can result in a type error or missing parameters in the api requests
  }

}
